<template>
  <div class="mx-auto p-4 w-full max-w-7xl">
    <div class="flex mt-4 text-base">
      <article class="flex-1">
        <div class="flex items-center">
          <h1 class="heading-1 mb-2">{{ course.code }}</h1>
          <div class="flex-grow"></div>
          <span
            v-if="enablePrintDocuments && course.id"
            class="link text-underline flex items-center"
            @click="() => downloadDocument(course.id)"
          >
            <BaseIcon
              v-if="isDownloadingDocument"
              icon="spinner"
              class="spinner inline-block mr-2"
            />
            <h2>Download as PDF</h2>
          </span>
        </div>
        <h2 class="heading-5 mb-2">{{ courseDisplayHeading }}</h2>
        <div class="text-small mb-10">
          <template v-if="!hideDepartments">
            <NuxtLink
              v-for="department of courseDepartments"
              :key="department.id"
              :to="`/departments/${encodeURIComponent(department.id)}/overview`"
            >
              {{ department.displayName || department.name }}
            </NuxtLink>
            <BaseIcon
              v-if="course.career"
              icon="circle"
              class="mx-4 icon-sm"
            />
          </template>
          <span>{{ course.career }}</span>
          <BaseIcon
            v-if="course.college"
            icon="circle"
            class="mx-4 icon-sm"
          />
          <span>{{ course.college }}</span>
          <RevisionSelector
            v-if="course.courseGroupId"
            class="ml-4 inline-flex items-center"
            entity-type="courses"
            :entity-id="course.courseGroupId"
          />
        </div>
        <HorizontalScroll
          v-if="!shouldHideTabs"
          class="border-b"
          :hide-arrows="tabs.length <= 1"
        >
          <BaseTabs
            :tabs="tabs"
            :value="activeTab"
            @input="activeTabKey = $event.key"
          >
            <template #default="{ tab, isActive }">
              <span
                class="tab-label text-black font-bold pb-2 transition-all duration-300 ease-in-out border-transparent"
                :class="{ 'tab-active heading-5 border-black': isActive }"
              >
                {{ tab.label }}
              </span>
            </template>
          </BaseTabs>
        </HorizontalScroll>
        <section>
          <transition name="fade-in">
            <DynamicFieldsRenderer
              v-if="fieldRows.length"
              :key="activeTemplateCard.id"
              :node-id="activeTemplateCard.id"
              class="mt-4 mb-2 -mx-2"
              :fields="fieldRows"
              :questions="Object.values(coursePageTemplate.questions)"
              :document-value="course"
              :value="course"
              entity-type="course"
            />
            <EmptyState
              v-else
              :key="`${activeTemplateCard.id}-empty`"
              class="bg-gray-50 rounded p-16 my-4"
            />
          </transition>
        </section>
      </article>
      <slot name="sidebar" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import DynamicFieldsRenderer from '@/components/contemporaryTheme/components/DynamicFieldsRenderer'
import EmptyState from '@/components/contemporaryTheme/components/Base/EmptyState'
import HorizontalScroll from '@/components/HorizontalScroll'
import { courseNameWithFallbacks } from '@/utils/entityNames'
import useFeatureFlags from '@/composables/useFeatureFlags'
import useDocumentsDownload from '@/composables/useDocumentsDownload'
import {
  HIDE_DEPARTMENTS,
  TITLE_INTERPOLATIONS,
  PRINT_DOCUMENTS,
} from '@/constants/featureFlags'
import useInterpolation from '@/composables/useInterpolation'
import { generateCourseLabel } from '@/utils/dataLabels'
import RevisionSelector from '@/components/RevisionSelector'
import { shouldAutoGeneratedNodeBeRendered } from '@/utils/generalUtils'
import useRevisions from '@/composables/useRevisions'

export default {
  components: {
    DynamicFieldsRenderer,
    EmptyState,
    HorizontalScroll,
    RevisionSelector
  },
  props: {
    course: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const {
      hideDepartments,
      enableTitleInterpolations,
      enablePrintDocuments,
    } = useFeatureFlags({
      hideDepartments: HIDE_DEPARTMENTS,
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
      enablePrintDocuments: PRINT_DOCUMENTS,
    })
    const { getInterpolatedFieldValue } = useInterpolation()
    const { downloadDocument, isDownloadingDocument } = useDocumentsDownload()
    const { shouldHideAutoGeneratedCards } = useRevisions()

    return {
      hideDepartments,
      courseNameWithFallbacks,
      enableTitleInterpolations,
      getInterpolatedFieldValue,
      enablePrintDocuments,
      downloadDocument,
      isDownloadingDocument,
      shouldHideAutoGeneratedCards,
    }
  },
  data() {
    return {
      activeTabKey: null,
    }
  },
  computed: {
    ...mapState('settings', ['coursePageTemplate']),
    ...mapState('departments', { departments: (state) => state.all }),
    activeTab() {
      return this.tabs.find((t) => t.key === this.activeTabKey) || this.tabs[0]
    },
    tabs() {
      return this.coursePageTemplate.template
        .filter((card) => shouldAutoGeneratedNodeBeRendered({ node: card, noAutoGeneratedCards: this.shouldHideAutoGeneratedCards }))
        .map((card) => ({
          key: card.id,
          label: card.config.title,
        }))
    },
    shouldHideTabs () {
      return this.tabs.length <= 1 && this.tabs.every(tab => !tab.label)
    },
    activeTemplateCard() {
      return this.coursePageTemplate.template.find(
        (card) => card.id === this.activeTab.key
      )
    },
    fieldRows() {
      return this.activeTemplateCard.children.reduce((acc, row) => {
        acc.push([...row.children])
        return acc
      }, [])
    },
    courseDepartments() {
      return (this.course.departments || []).map(
        (dep) =>
          typeof dep === 'object'
            ? dep
            : this.departments.find((el) => el.id === dep) || {
                id: dep,
                displayName: dep,
              }
      )
    },
    courseDisplayHeading() {
      return generateCourseLabel(this.course, undefined, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
        nameOnlyFallback: true,
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/contemporaryTheme/tabbedTemplate.scss';
</style>
